// src/lib/analytics/interaction.ts
/**
 * What counts as a visitor starting to answer a form.
 *
 * Task 3.1 of docs/form-dropoff-visibility_plan.md sends one `form_started`
 * event the first time somebody puts the cursor in a field. Both renderers
 * send it (`src/components/forms/LiveForm.tsx` and the vanilla
 * `src/scripts/kiroku-form.ts`), and `getStepFunnelData`'s neighbour on the
 * results page counts them as one series, so the two have to agree on which
 * focus counts. This module is that agreement, imported by both rather than
 * written twice: the whole plan exists because two renderers quietly disagreed
 * about which events they sent.
 *
 * Focus is the signal rather than a keystroke or a change, because it is the
 * earliest and the most inclusive: typing requires focus, clicking a checkbox,
 * a radio or a select focuses it, and a visitor who tabs into a field and
 * retreats has still engaged with the form in the way this number is about.
 */

/**
 * Input types that are not somebody answering a question.
 *
 * A submit or reset button is the form's own furniture, and tabbing to it
 * without filling anything in is the opposite of starting: it is the case
 * `form_started` exists to tell apart from a real start. `hidden` is never
 * focusable by a person, so a focus on one came from a script.
 */
const NON_ANSWERING_INPUT_TYPES = new Set([
  "hidden",
  "submit",
  "button",
  "reset",
  "image",
]);

/**
 * Is this focus target one of the owner's own questions?
 *
 * Takes an `EventTarget` because that is what both a React `FocusEvent` and a
 * DOM `focusin` hand over, so neither renderer has to cast before asking.
 *
 * The honeypot is excluded deliberately. It is hidden from people
 * (`display:none`, `tabindex="-1"`), so a focus on it came from software, and
 * counting it would inflate "started" on exactly the forms that get the most
 * bot traffic. Both renderers mark it with `data-kiroku-honeypot="true"`.
 */
export function isAnsweringControl(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null;
  if (!element || typeof element.tagName !== "string") return false;
  if (element.getAttribute?.("data-kiroku-honeypot") === "true") return false;

  const tag = element.tagName.toLowerCase();
  if (tag === "select" || tag === "textarea") return true;
  if (tag !== "input") return false;

  // An <input> with no type attribute is a text box, which is why the default
  // here is "text" rather than "".
  const type = (element.getAttribute("type") || "text").toLowerCase();
  return !NON_ANSWERING_INPUT_TYPES.has(type);
}

/**
 * Which of the owner's questions a focus landed on, by **name**.
 *
 * Task 3.2 of docs/form-dropoff-visibility_plan.md needs the name of the last
 * field somebody touched before they left, which is the only in-form signal
 * that exists on a single-step form. It reuses `isAnsweringControl` rather
 * than asking its own question, so a control that does not count as a start
 * cannot count as a departure point either: the submit button and the honeypot
 * are excluded here by construction.
 *
 * **A name, never a value.** The element's `value` is never read, here or by
 * any caller (CLAUDE.md §13, and the plan's hard constraint).
 *
 * A multi-option checkbox posts under `colours[]` so the browser sends every
 * box as one array, while the field the owner defined is `colours`
 * (`LiveForm.tsx:421`). The suffix is stripped so both renderers and the
 * dashboard group on the name the owner typed; without it a checkbox group
 * would appear in the drop-off list as a field that does not exist on the form.
 */
export function answeringFieldName(target: EventTarget | null): string | null {
  if (!isAnsweringControl(target)) return null;
  const name = (target as HTMLElement).getAttribute?.("name") || "";
  if (!name) return null;
  return name.endsWith("[]") ? name.slice(0, -2) : name;
}
